/**
 * Formulário de um perigo: o que pode acontecer, as quatro perguntas, o resultado na hora e o
 * bloco opcional "após adequações" (os quatro fatores ou nenhum).
 */
import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import type { HrnScale, MachineHazardAssessment, MachineHazardAssessmentFormData } from '../../../../types/machine';
import { HrnFactorFields } from './HrnFactorFields';
import { HrnResult } from './HrnResult';
import {
  EMPTY_HRN_ANSWERS,
  HRN_FACTOR_KEYS,
  answeredCount,
  calculateHrn,
  completeFactors,
  type HrnAnswers,
} from './hrnCalculation';

interface HazardFormProps {
  scale: HrnScale;
  /** Perigo em edição; ausente no perigo novo. */
  initial?: MachineHazardAssessment;
  saving: boolean;
  onSave: (data: MachineHazardAssessmentFormData) => void;
  onCancel: () => void;
}

type SourceFactors = Record<(typeof HRN_FACTOR_KEYS)[number], number | null>;

/** Só vale a resposta que ainda existe na escala vigente: perigo antigo precisa ser respondido de novo. */
function answersFrom(scale: HrnScale, source: SourceFactors | undefined): HrnAnswers {
  if (!source) return EMPTY_HRN_ANSWERS;
  const answers = { ...EMPTY_HRN_ANSWERS };
  for (const factor of scale.factors) {
    const value = source[factor.key];
    if (value !== null && factor.values.includes(Number(value))) answers[factor.key] = Number(value);
  }
  return answers;
}

export const HazardForm: React.FC<HazardFormProps> = ({ scale, initial, saving, onSave, onCancel }) => {
  const [hazard, setHazard] = useState(initial?.hazard ?? '');
  const [current, setCurrent] = useState<HrnAnswers>(() => answersFrom(scale, initial));
  const [residual, setResidual] = useState<HrnAnswers>(() =>
    answersFrom(
      scale,
      initial && {
        likelihood: initial.residualLikelihood,
        exposureFrequency: initial.residualExposureFrequency,
        harmSeverity: initial.residualHarmSeverity,
        peopleExposed: initial.residualPeopleExposed,
      }
    )
  );

  const currentResult = useMemo(() => calculateHrn(current, scale), [current, scale]);
  const residualResult = useMemo(() => calculateHrn(residual, scale), [residual, scale]);
  const residualPartial = answeredCount(residual) > 0 && answeredCount(residual) < 4;
  const canSave = hazard.trim() !== '' && completeFactors(current) !== null && !residualPartial;

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    const factors = completeFactors(current);
    if (!canSave || factors === null) return;
    const residualFactors = completeFactors(residual);
    onSave({
      hazard: hazard.trim(),
      ...factors,
      residualLikelihood: residualFactors?.likelihood ?? null,
      residualExposureFrequency: residualFactors?.exposureFrequency ?? null,
      residualHarmSeverity: residualFactors?.harmSeverity ?? null,
      residualPeopleExposed: residualFactors?.peopleExposed ?? null,
    });
  };

  return (
    <Box component="form" onSubmit={submit} noValidate aria-label="Apreciação do perigo" sx={{ display: 'grid', gap: 3 }}>
      <TextField
        fullWidth
        required
        label="Perigo"
        placeholder="Ex.: esmagamento de mãos na zona de prensagem"
        value={hazard}
        onChange={(event) => setHazard(event.target.value)}
        helperText="Descreva o que pode machucar alguém nesta máquina."
      />

      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="h6" component="h2">
          Como é hoje
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Responda os quatro fatores. O resultado aparece abaixo, na hora.
        </Typography>
        <HrnFactorFields scale={scale} answers={current} onChange={setCurrent} idPrefix="hrn-hoje" />
        <HrnResult
          title="Resultado (HRN)"
          result={currentResult}
          bands={scale.bands}
          placeholder="Responda os quatro fatores"
        />
      </Box>

      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="h6" component="h2">
          Depois das adequações previstas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Opcional. Informe como ficam os quatro fatores quando as adequações estiverem feitas — ou deixe todos em
          branco. O laudo mostra os dois resultados lado a lado.
        </Typography>
        <HrnFactorFields
          scale={scale}
          answers={residual}
          onChange={setResidual}
          idPrefix="hrn-apos"
          allowClear
          labelSuffix=" (após adequações)"
        />
        {residualPartial && (
          <Alert severity="warning">Informe os quatro fatores de &quot;depois das adequações&quot; ou nenhum.</Alert>
        )}
        <HrnResult
          title="Resultado após adequações"
          result={residualResult}
          bands={scale.bands}
          placeholder="Não informado"
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button type="submit" variant="contained" disabled={!canSave || saving} sx={{ minHeight: 44 }}>
          Salvar perigo
        </Button>
        <Button type="button" variant="outlined" onClick={onCancel} disabled={saving} sx={{ minHeight: 44 }}>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default HazardForm;
